import React from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link as RouterLink } from "react-router-dom";
import RollingNumber from "../../components/RollingNumber.js";
import { StyledLink } from "../../components/CustomMUI.js";
import { CHIP_ROW_HEIGHT, SECTION_GAP, emptyTabOffsetSx } from "./Header.js";
import { toast } from "react-toastify";
import {
  formatCount,
  getContentLabel,
  getContentOptions,
} from "../../utils/utils.js";

const CONTENT_TYPES = ["movie", "tvshow", "book"];
// Types the stats page has stats for; All opens on movies'
const TYPES_WITH_STATS = ["all", "movie"];

// A content type's icon and how many of it the list shows, plus the type's
// name where there's room ("12 movies")
const TypeCount = ({ type, count = 0, showLabel }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    {React.cloneElement(getContentOptions()[type].icon, {
      sx: { fontSize: 16 },
    })}
    <span>
      <RollingNumber value={count} />
      {showLabel &&
        ` ${getContentLabel({ type, plural: count !== 1, lowercase: true })}`}
    </span>
  </Stack>
);

/**
 * The queue's length in its own unit: movie runtime as hours (minutes under
 * an hour), TV episodes, book pages. "~" marks episodes or pages as a
 * minimum, when some titles have no length on record — not hours, which
 * read as rounded anyway. Null for an empty queue.
 */
const formatToClear = ({ unit, value, complete }) => {
  if (!value) return null;
  if (unit === "minutes") {
    const length = value < 60 ? `${value}m` : `${Math.round(value / 60)}h`;
    return (
      <>
        <RollingNumber value={length} /> to watch
      </>
    );
  }
  // Compact, as a long queue runs to thousands: "20.7k pages"
  const amount = `${complete ? "" : "~"}${formatCount(value)}`;
  return (
    <>
      <RollingNumber value={amount} />{" "}
      {unit === "episodes" ? "episodes to watch" : "pages to read"}
    </>
  );
};

/**
 * The tab's own stat, beside its count — only on a single content type's
 * tab, which is all the backend sends one for: average rating (finished),
 * what's left to get through (queued), or starts this month (started).
 * Null for stopped, or when the stat would be empty. `wide` spells out
 * "average" where there's room.
 */
const getSecondaryStat = (summary, wide) => {
  if (summary.averageRating != null) {
    return (
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        spacing={0.25}
      >
        <StarIcon sx={{ fontSize: 15 }} />
        <span>
          <RollingNumber value={summary.averageRating.toFixed(1)} />{" "}
          {wide ? "average" : "avg"}
        </span>
      </Stack>
    );
  }
  if (summary.toClear) return formatToClear(summary.toClear);
  if (summary.startedThisMonth) {
    return (
      <>
        <RollingNumber value={summary.startedThisMonth} /> started this month
      </>
    );
  }
  return null;
};

// Separates the strip's parts; never gives way when space runs short
const Dot = () => (
  <Box component="span" sx={{ color: "text.disabled", flexShrink: 0 }}>
    ·
  </Box>
);

/**
 * Whose library this is, leading the strip on someone else's: avatar and
 * @username, each linking to their profile, styled as on Explore's activity.
 * A long name gives way (with an ellipsis), so the counts always fit.
 */
const OwnerLabel = ({ username, avatarUrl, isWide }) => (
  <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
    <RouterLink
      to={`/u/${username}`}
      style={{ display: "flex", marginRight: 6 }}
    >
      <Avatar
        src={avatarUrl}
        alt={username}
        imgProps={{ referrerPolicy: "no-referrer" }}
        sx={{ width: 20, height: 20 }}
      />
    </RouterLink>
    <StyledLink
      component={RouterLink}
      to={`/u/${username}`}
      variant="body2"
      sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
    >
      @{username}
    </StyledLink>
    {isWide && (
      <Box component="span" sx={{ flexShrink: 0 }}>
        's library
      </Box>
    )}
  </Box>
);

/**
 * A quiet strip between the Track header and its list: how many the list
 * shows as filtered, by content type icon (one per type on All), and on a
 * single type's tab a stat for it after a dot (not on someone else's).
 *
 * On your own library it ends in a button to your full stats. On someone
 * else's it leads with whose library it is and ends in a way back to your
 * own; on the demo library, with a sign-up prompt.
 *
 * An empty tab (which EmptyLibrary covers) shows only a spacer, unless
 * there's an owner to name.
 *
 * @param {object} [summary] - From fetchLibrarySummary; undefined while loading
 * @param {string} contentType - The tab's type, or "all"
 * @param {string} [statsPath] - Where the stats button goes; no button without one
 * @param {object} [owner] - { username, avatarUrl } of someone else's library
 * @param {boolean} [isDemo] - Labels the strip as the demo library
 * @param {function} [onExit] - Back to your own library
 * @param {string} [viewerAvatarUrl] - Your avatar, on the button back to it
 * @param {function} [onSignUp] - The demo library's sign-up prompt
 */
export default function LibrarySummary({
  summary,
  contentType,
  statsPath,
  owner,
  isDemo,
  onExit,
  viewerAvatarUrl,
  onSignUp,
}) {
  // Desktop has room for words the phone layout abbreviates or leaves out
  const isWide = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const totals = summary?.totals ?? {};
  const hasCounts = !!summary && Object.values(totals).some(Boolean);
  const label = owner ? (
    <OwnerLabel {...owner} isWide={isWide} />
  ) : isDemo ? (
    <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
      Demo library
    </Box>
  ) : null;
  // Holds the strip's place so the empty state lines up with Curate's
  if (summary && !hasCounts && !label) {
    return <Box sx={emptyTabOffsetSx} />;
  }

  const hasStats = TYPES_WITH_STATS.includes(contentType);
  // Left out on someone else's library, leaving their name the room
  const secondary =
    hasCounts && contentType !== "all" && !owner
      ? getSecondaryStat(summary, isWide)
      : null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      // Plain text on the page background, flush with the list's edges:
      // reads as a title for the list without competing with it
      sx={{
        // As tall as the filter chip row, for even spacing when both show
        height: CHIP_ROW_HEIGHT,
        // The same gap as above it, below the header
        mb: `${SECTION_GAP}px`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ minWidth: 0, typography: "body2", whiteSpace: "nowrap" }}
      >
        {label}
        {label && (hasCounts || !summary) && <Dot />}

        {!summary ? (
          <Skeleton width={96} />
        ) : (
          hasCounts && (
            // Numbers roll to each new value as filters change (see RollingNumber)
            <Stack
              direction="row"
              alignItems="center"
              // Wider on All, to keep each type's count clearly its own
              spacing={contentType === "all" ? 1.25 : 0}
              sx={{ flexShrink: 0 }}
            >
              {contentType === "all" ? (
                // Every type in the tab, even one the filters leave at 0
                CONTENT_TYPES.filter((type) => totals[type]).map((type) => (
                  <TypeCount
                    key={type}
                    type={type}
                    count={summary.counts[type]}
                    showLabel={isWide}
                  />
                ))
              ) : (
                <TypeCount
                  type={contentType}
                  count={summary.counts[contentType]}
                  showLabel={isWide}
                />
              )}
            </Stack>
          )
        )}

        {secondary && (
          <>
            <Dot />
            {/* Gives way on a narrow phone, so the count and the button at
                the end always fit */}
            <Box
              component="span"
              sx={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {secondary}
            </Box>
          </>
        )}
      </Stack>

      {statsPath ? (
        <Tooltip title="See all stats">
          <IconButton
            // TV and books have no stats yet: a toast rather than a dead end
            {...(hasStats
              ? { component: RouterLink, to: statsPath }
              : {
                  onClick: () =>
                    toast.info(
                      `Stats for ${getContentLabel({ type: contentType, plural: true, lowercase: true })} coming soon!`,
                    ),
                })}
            aria-label="See all stats"
            size="small"
            edge="end"
            // The strip's text colour rather than the theme's primary
            color="inherit"
            // A pill rather than a circle, now it holds two icons
            // Negative margin lets it overhang the slim strip rather than
            // set its height, keeping a full-size tap target
            sx={{ flexShrink: 0, borderRadius: 2, my: -0.375 }}
          >
            <BarChartRoundedIcon fontSize="small" />
            <ChevronRightRoundedIcon fontSize="small" sx={{ ml: -0.25 }} />
          </IconButton>
        </Tooltip>
      ) : onExit ? (
        // On to your own library: pairs with the owner's avatar on the left
        <Tooltip title="Back to your library">
          <IconButton
            aria-label="Back to your library"
            size="small"
            edge="end"
            color="inherit"
            onClick={onExit}
            // A pill like the stats button, overhanging the strip the same way
            sx={{ flexShrink: 0, borderRadius: 2, my: -0.375 }}
          >
            {/* Tucked slightly over the avatar's edge, drawn above it */}
            <ArrowForwardRoundedIcon
              fontSize="small"
              sx={{
                mr: -0.5,
                position: "relative",
                zIndex: 1,
                // Outlined in its own colour, for a bolder arrow than the
                // icon set offers
                stroke: "currentColor",
                strokeWidth: 1,
              }}
            />
            <Avatar
              src={viewerAvatarUrl}
              alt="You"
              imgProps={{ referrerPolicy: "no-referrer" }}
              sx={{ width: 20, height: 20 }}
            />
          </IconButton>
        </Tooltip>
      ) : (
        onSignUp && (
          <Button
            size="small"
            onClick={onSignUp}
            sx={{ flexShrink: 0, py: 0, whiteSpace: "nowrap" }}
          >
            Sign up
          </Button>
        )
      )}
    </Stack>
  );
}
